import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface ScenarioRow {
  readonly testScenarioId: string;
  readonly scenarioCode: string;
  readonly title: string;
  readonly priority: string;
  readonly complexity: string;
  readonly riskRating: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'ACTIVE', 'ARCHIVED'];
const PRIORITIES = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CREATE_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

/**
 * M34 — Scenario Management (§31 / §8). Register of reusable test scenarios
 * (T449) with priority, complexity and risk rating and the draft→active→archived
 * lifecycle. Supports authoring a scenario and driving it — activate, archive and
 * delete — each wired to the guarded /v1/qms/test-scenarios write API. Filterable
 * by status and priority.
 */
export function ScenarioManagement(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ScenarioRow[]>([]);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState('MEDIUM');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    const qs = params.toString();
    apiRequest<{ data: ScenarioRow[] }>(`/v1/qms/test-scenarios${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, priority, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.scenario.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/test-scenarios`, {
        method: 'POST',
        body: { title: newTitle.trim(), priority: newPriority },
        token,
      });
      setNotice(t('m34.scenario.created', { title: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_SCENARIO_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: ScenarioRow, path: string, label: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-scenarios/${r.testScenarioId}/${path}`, { method: 'POST', body: {}, token });
      setNotice(t('m34.scenario.notice', { code: r.scenarioCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_SCENARIO_MANAGE'));
    }
  };

  const remove = async (r: ScenarioRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-scenarios/${r.testScenarioId}`, { method: 'DELETE', token });
      setNotice(t('m34.scenario.notice', { code: r.scenarioCode, label: t('m34.scenario.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_SCENARIO_MANAGE'));
    }
  };

  return (
    <main aria-label="scenario-management">
      <h1>{t('m34.scenario.title')}</h1>

      <section aria-label="create-scenario">
        <h2>{t('m34.scenario.createHeading')}</h2>
        <form aria-label="scenario-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-scenario-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m34.common.priority')}
            <select aria-label="field-scenario-priority" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {CREATE_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-scenario" disabled={busy}>{busy ? t('m34.scenario.creating') : t('m34.scenario.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="scenario-action-error">{actionError}</p>}
        {notice && <p aria-label="scenario-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.common.priority')}
          <select aria-label="filter-priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {PRIORITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="scenario-count">{t('m34.scenario.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="scenario-table"
            rows={rows}
            rowKey={(r) => r.testScenarioId}
            columns={[
              { key: 'scenarioCode', header: t('m34.common.code'), render: (r) => r.scenarioCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'priority', header: t('m34.common.priority'), render: (r) => r.priority },
              { key: 'complexity', header: t('m34.scenario.colComplexity'), render: (r) => r.complexity },
              { key: 'riskRating', header: t('m34.scenario.colRisk'), render: (r) => r.riskRating },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`activate-${r.scenarioCode}`} onClick={() => void act(r, 'activate', t('m34.scenario.act.activated'))}>{t('m34.scenario.btnActivate')}</Button>
                  )}
                  {r.status === 'ACTIVE' && (
                    <Button variant="danger" type="button" aria-label={`archive-${r.scenarioCode}`} onClick={() => void act(r, 'archive', t('m34.scenario.act.archived'))}>{t('m34.scenario.btnArchive')}</Button>
                  )}
                  {r.status !== 'ARCHIVED' && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.scenarioCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
